import clsx from 'clsx';
import styles from 'components/Tasks/Detail/TaskDetail.module.scss';
import Spinner from 'components/UI/V3/Spinner';
import React, { useEffect, useState } from 'react';
import { TaskDetailEmptyState } from './EmptyState/TaskDetailEmptyState';
import { Task } from '@spike/task-model';
import { TaskCard } from './TaskCard';
import { AddonTaskCard } from './AddonTaskCard';
import moment from 'moment-timezone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import useNonInitialEffect from '@versiondos/hooks';
import { RootState } from 'store';
import { useDispatch, useSelector } from 'react-redux';
import { TasksStatus } from '@spike/tasks-action';
import { useApiClientWrapper, useTimeZone } from 'hooks';
import { OrdersStatus, reloadOrderThunk } from '@spike/orders-action';
import { AppointmentOrderItem, AddonTask } from '@spike/order-model';

interface TaskDetailProps {
    tasks?: Task[];
    addonTasks?: AddonTask[];
    appointmentItems?: AppointmentOrderItem[];
    className?: string;
    empty?: boolean;
    readOnly?: boolean;
    onEdit: (task: Task) => void;
    onAddTask: () => void;
}

interface GroupedTasks {
    [date: string]: Task[];
}

interface GroupedAddonTasks {
    [date: string]: AddonTask[];
}

export const TaskDetail: React.FC<TaskDetailProps> = props => {
    const apiClientWrapper = useApiClientWrapper();
    const dispatch = useDispatch();
    const timezone = useTimeZone();
    const tasksStatus = useSelector<RootState, TasksStatus>(state => state.tasks.status);
    const ordersStatus = useSelector<RootState, OrdersStatus>(state => state.orders.status);
    const [renderState, setRenderState] = React.useState<'loading' | 'empty' | 'content'>('loading');
    const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

    useEffect(() => {
        const timer = setTimeout(() => {
            const hasTasks =
                (props.tasks && props.tasks.length > 0) || (props.addonTasks && props.addonTasks.length > 0);
            if (hasTasks) {
                setRenderState('content');
            } else if (props.empty) {
                setRenderState('empty');
            } else {
                setRenderState('content');
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [props.tasks, props.addonTasks, props.empty]);

    useNonInitialEffect(() => {
        switch (tasksStatus) {
            case TasksStatus.Completing:
                setRenderState('loading');
                break;
            case TasksStatus.CompleteSuccess:
                dispatch(reloadOrderThunk(apiClientWrapper));
                if ((props.tasks && props.tasks.length > 0) || (props.addonTasks && props.addonTasks.length > 0)) {
                    setRenderState('content');
                } else if (props.empty) {
                    setRenderState('empty');
                } else {
                    setRenderState('content');
                }
                break;
            case TasksStatus.Error:
                dispatch(reloadOrderThunk(apiClientWrapper));
                setRenderState('empty');
                break;
        }
    }, [tasksStatus]);

    useNonInitialEffect(() => {
        switch (ordersStatus) {
            case OrdersStatus.Reloading:
                setRenderState('loading');
                break;
            case OrdersStatus.ReloadSuccess:
            case OrdersStatus.Error:
                if (
                    (!props.tasks || props.tasks.length === 0) &&
                    (!props.addonTasks || props.addonTasks.length === 0)
                ) {
                    setRenderState('empty');
                } else {
                    setRenderState('content');
                }
                break;
        }
    }, [ordersStatus]);

    const groupedTasks = React.useMemo(() => {
        const grouped: GroupedTasks = {};

        props.tasks?.forEach(task => {
            if (task.taskDate) {
                const dateKey = task.taskDate.clone().tz(timezone).format('YYYY-MM-DD');
                if (!grouped[dateKey]) {
                    grouped[dateKey] = [];
                }
                grouped[dateKey].push(task);
            }
        });

        return grouped;
    }, [props.tasks, timezone]);

    const groupedAddonTasks = React.useMemo(() => {
        const grouped: GroupedAddonTasks = {};

        props.addonTasks?.forEach(task => {
            if (task.taskDate) {
                const dateKey = task.taskDate.clone().tz(timezone).format('YYYY-MM-DD');
                if (!grouped[dateKey]) {
                    grouped[dateKey] = [];
                }
                grouped[dateKey].push(task);
            }
        });

        return grouped;
    }, [props.addonTasks, timezone]);

    const groupedDates = React.useMemo(() => {
        const dates = new Set([...Object.keys(groupedTasks), ...Object.keys(groupedAddonTasks)]);
        return Array.from(dates).sort((a, b) => moment(a).diff(moment(b)));
    }, [groupedTasks, groupedAddonTasks]);

    useEffect(() => {
        if (groupedDates.length > 0 && expandedDates.size === 0) {
            setExpandedDates(new Set([groupedDates[0]]));
        }
    }, [groupedDates]);

    const toggleDateExpansion = (date: string) => {
        setExpandedDates(prev => {
            const newSet = new Set(prev);
            if (newSet.has(date)) {
                newSet.delete(date);
            } else {
                newSet.add(date);
            }
            return newSet;
        });
    };

    const renderContent = () => {
        switch (renderState) {
            case 'loading':
                return (
                    <div className={styles.loading}>
                        <Spinner />
                    </div>
                );
            case 'empty':
                return (
                    <TaskDetailEmptyState
                        onAddTask={props.onAddTask}
                        readOnly={props.readOnly}
                    />
                );
            case 'content':
                return (
                    <div className={styles.tasksContainer}>
                        {groupedDates.map((date, index) => {
                            const isExpanded = expandedDates.has(date);
                            const formattedDate = moment(date).format('dddd, DD MMM YYYY');
                            const isLast = index === groupedDates.length - 1;
                            const shouldShowDivider = !isExpanded && !isLast;
                            const tasksForDate = groupedTasks[date] ?? [];
                            const addonTasksForDate = groupedAddonTasks[date] ?? [];
                            const timelineItems = [
                                ...tasksForDate.map(task => ({
                                    kind: 'task' as const,
                                    data: task,
                                    sortTime: task.taskDate?.valueOf() ?? 0
                                })),
                                ...addonTasksForDate.map(addonTask => ({
                                    kind: 'addon' as const,
                                    data: addonTask,
                                    sortTime: addonTask.taskDate?.valueOf() ?? 0
                                }))
                            ].sort((left, right) => left.sortTime - right.sortTime);

                            return (
                                <div
                                    key={date}
                                    className={styles.dateGroup}
                                >
                                    <div
                                        className={styles.dateHeader}
                                        onClick={() => toggleDateExpansion(date)}
                                    >
                                        <span className={styles.dateText}>{formattedDate}</span>
                                        <span className={clsx(styles.chevron)}>
                                            {isExpanded ? (
                                                <FontAwesomeIcon icon={faChevronUp} />
                                            ) : (
                                                <FontAwesomeIcon icon={faChevronDown} />
                                            )}
                                        </span>
                                    </div>

                                    {shouldShowDivider && <div className={styles.divider} />}

                                    {isExpanded && (
                                        <div className={styles.tasksList}>
                                            {timelineItems.map(item =>
                                                item.kind === 'addon' ? (
                                                    <AddonTaskCard
                                                        key={`addon-${item.data.id}`}
                                                        task={item.data}
                                                        readOnly={props.readOnly}
                                                    />
                                                ) : (
                                                    <TaskCard
                                                        key={`task-${item.data.id}`}
                                                        task={item.data}
                                                        tasks={tasksForDate}
                                                        readOnly={props.readOnly}
                                                        appointmentItem={
                                                            props.appointmentItems?.find(
                                                                appointmentItem =>
                                                                    appointmentItem.appointment.id ===
                                                                    item.data.appointmentTaskId
                                                            )!
                                                        }
                                                        onEdit={props.onEdit}
                                                    />
                                                )
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                );
        }
    };

    return <div className={clsx(styles.root, props.className)}>{renderContent()}</div>;
};
